import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoJsonLd from '../../components/feature/SeoJsonLd';
import { supabase } from '../../lib/supabase';

const FORM_URL = 'https://readdy.ai/api/form/d6n5gidv117fnkj2hd60';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Devenir partenaire Localygo – Restaurant, Boutique, Commerce local',
  description: 'Rejoignez Localygo en tant que partenaire restaurant ou boutique. Commission équitable, visibilité locale, tableau de bord complet et support dédié.',
  url: `${SITE_URL}/partenaire`,
};

const avantages = [
  {
    icon: 'ri-percent-line',
    title: 'Commission équitable',
    desc: 'Profitez d\'une commission parmi les plus basses du marché. Gardez l\'essentiel de vos revenus et développez votre activité sereinement.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Visibilité locale',
    desc: 'Soyez mis en avant auprès des habitants de votre quartier. Localygo booste votre présence locale et attire de nouveaux clients.',
  },
  {
    icon: 'ri-dashboard-3-line',
    title: 'Tableau de bord complet',
    desc: 'Gérez vos commandes, votre menu, vos stocks et vos statistiques depuis une interface intuitive et tout-en-un.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Support dédié',
    desc: 'Un interlocuteur dédié vous accompagne à chaque étape : onboarding, formation, et assistance quotidienne 7j/7.',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Croissance rapide',
    desc: 'Nos partenaires constatent en moyenne +35% de commandes dès le premier mois. Développez votre chiffre d\'affaires sans effort.',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Promotions & marketing',
    desc: 'Accédez à des outils de promotion intégrés : offres spéciales, codes promo, notifications push à vos clients fidèles.',
  },
];

const etapes = [
  {
    num: '01',
    icon: 'ri-file-text-line',
    title: 'Soumettez votre candidature',
    desc: 'Remplissez le formulaire en ligne en 5 minutes. Renseignez votre type de commerce, votre ville et vos informations de contact.',
  },
  {
    num: '02',
    icon: 'ri-user-search-line',
    title: 'Étude de votre dossier',
    desc: 'Notre équipe examine votre candidature sous 48h et vous contacte pour un échange personnalisé.',
  },
  {
    num: '03',
    icon: 'ri-settings-3-line',
    title: 'Configuration de votre espace',
    desc: 'Nous vous aidons à configurer votre profil, importer votre menu ou catalogue, et paramétrer vos horaires.',
  },
  {
    num: '04',
    icon: 'ri-rocket-line',
    title: 'Lancez-vous !',
    desc: 'Votre établissement est en ligne. Commencez à recevoir des commandes et développez votre clientèle locale.',
  },
];

const typeCommerce = [
  'Restaurant',
  'Boulangerie / Pâtisserie',
  'Épicerie / Superette',
  'Boutique de mode',
  'Pharmacie / Parapharmacie',
  'Fleuriste',
  'Traiteur',
  'Cave à vins / Spiritueux',
  'Autre',
];

export default function DevenirPartenairePage() {
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    commerce: '',
    nomEtablissement: '',
    ville: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    if (!formData.prenom || !formData.nom || !formData.email || !formData.telephone || !formData.commerce) {
      setError('Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setLoading(true);
    try {
      const { error: insertError } = await supabase
        .from('candidatures_partenaires')
        .insert([{
          prenom: formData.prenom,
          nom: formData.nom,
          email: formData.email,
          telephone: formData.telephone,
          type_commerce: formData.commerce,
          nom_etablissement: formData.nomEtablissement,
          ville: formData.ville,
          message: formData.message,
        }]);

      if (insertError) throw insertError;
      
      setSubmitted(true);
    } catch (err) {
      console.error('Erreur lors de l\'envoi:', err);
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoJsonLd data={orgSchema} id="schema-partenaire" />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[640px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/e79de5558ca05c119883e0c8b5f6defd.jpg"
            alt="Devenir partenaire Localygo"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-36">
          <div className="max-w-2xl">
            <span className="inline-block bg-teal-500/20 border border-teal-400/40 text-teal-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-widest">
              Programme partenaires Localygo
            </span>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Développez votre<br />
              <span className="text-teal-400">commerce local</span>
            </h1>
            <p className="text-lg text-white/80 mb-10 leading-relaxed">
              Rejoignez plus de 500 restaurants et boutiques qui font confiance à Localygo pour toucher leurs clients locaux, gérer leurs commandes et booster leur chiffre d'affaires.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#candidature"
                className="bg-teal-500 hover:bg-teal-400 text-white px-8 py-4 rounded-full font-semibold text-base transition-all shadow-lg hover:shadow-teal-500/30 cursor-pointer whitespace-nowrap"
              >
                Devenir partenaire
              </a>
              <a
                href="#avantages"
                className="border border-white/50 text-white hover:bg-white/10 px-8 py-4 rounded-full font-semibold text-base transition-all cursor-pointer whitespace-nowrap"
              >
                Découvrir les avantages
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-teal-600 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            {[
              { value: '500+', label: 'Partenaires actifs' },
              { value: '+35%', label: 'Commandes en moyenne' },
              { value: '48h', label: 'Délai d\'intégration' },
              { value: '7j/7', label: 'Support dédié' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-3xl font-extrabold mb-1">{stat.value}</div>
                <div className="text-sm text-teal-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section id="avantages" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Pourquoi rejoindre Localygo ?</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Une plateforme pensée pour les commerçants locaux, avec des outils puissants et une approche humaine.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {avantages.map((a) => (
              <div key={a.title} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all group cursor-default">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-5 group-hover:bg-teal-600 transition-colors">
                  <i className={`${a.icon} text-2xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{a.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Comment ça marche ?</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto">
              Rejoindre Localygo en tant que partenaire est simple, rapide et sans engagement.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {etapes.map((e) => (
              <div key={e.num} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-600 text-white rounded-full mx-auto mb-5">
                  <i className={`${e.icon} text-2xl`}></i>
                </div>
                <span className="text-xs font-bold text-teal-500 uppercase tracking-widest mb-2 block">{e.num}</span>
                <h3 className="text-base font-bold text-gray-900 mb-3">{e.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Illustration / Aperçu dashboard */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-teal-600 text-sm font-semibold uppercase tracking-widest mb-3 block">Tableau de bord partenaire</span>
              <h2 className="text-4xl font-extrabold text-gray-900 mb-6 leading-tight">
                Tout gérer depuis<br />une seule interface
              </h2>
              <p className="text-gray-500 text-base leading-relaxed mb-8">
                Votre espace partenaire Localygo vous donne une vue complète sur votre activité : commandes en temps réel, gestion du menu, statistiques de vente, avis clients et bien plus encore.
              </p>
              <ul className="space-y-4">
                {[
                  { icon: 'ri-shopping-bag-line', text: 'Gestion des commandes en temps réel' },
                  { icon: 'ri-restaurant-line', text: 'Mise à jour du menu en un clic' },
                  { icon: 'ri-bar-chart-2-line', text: 'Statistiques et rapports détaillés' },
                  { icon: 'ri-star-line', text: 'Suivi des avis et de la satisfaction client' },
                  { icon: 'ri-coupon-line', text: 'Création de promotions et offres spéciales' },
                ].map((item) => (
                  <li key={item.text} className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                      <i className={`${item.icon} text-teal-600 text-base`}></i>
                    </span>
                    <span className="text-gray-700 text-sm font-medium">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20restaurant%20dashboard%20interface%20on%20laptop%20screen%2C%20clean%20UI%20with%20charts%20and%20order%20management%2C%20teal%20color%20scheme%2C%20professional%20workspace%2C%20warm%20ambient%20lighting%2C%20high%20quality%20product%20photography&width=700&height=500&seq=dashboard-partenaire&orientation=landscape"
                  alt="Tableau de bord partenaire Localygo"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className="ri-arrow-up-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-gray-900">+35%</div>
                  <div className="text-xs text-gray-500">de commandes en moyenne</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section id="candidature" className="py-24 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Rejoignez-nous dès maintenant</h2>
            <p className="text-gray-500 text-lg">
              Remplissez ce formulaire et notre équipe vous contactera sous 48h pour démarrer votre intégration.
            </p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                <i className="ri-check-line text-4xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Candidature envoyée !</h3>
              <p className="text-gray-500 text-base mb-6">
                Merci <strong>{formData.prenom}</strong> ! Nous avons bien reçu votre candidature. Notre équipe vous contactera dans les 48h pour la suite.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({ prenom: '', nom: '', email: '', telephone: '', commerce: '', nomEtablissement: '', ville: '', message: '' });
                }}
                className="bg-teal-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Nouvelle candidature
              </button>
            </div>
          ) : (
            <form
              data-readdy-form
              id="candidature-partenaire"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl shadow-lg p-10 space-y-6"
            >
              {/* Prénom / Nom */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prénom <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    placeholder="Votre prénom"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Email / Téléphone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre@email.fr"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Téléphone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    placeholder="06 XX XX XX XX"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Type de commerce */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Type de commerce <span className="text-red-500">*</span></label>
                <select
                  name="commerce"
                  value={formData.commerce}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                  required
                >
                  <option value="">Sélectionnez votre type de commerce</option>
                  {typeCommerce.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Nom établissement / Ville */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom de l'établissement</label>
                  <input
                    type="text"
                    name="nomEtablissement"
                    value={formData.nomEtablissement}
                    onChange={handleChange}
                    placeholder="Ex : Le Petit Bistrot"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ville</label>
                  <input
                    type="text"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    placeholder="Ex : Lyon, Paris, Bordeaux..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message (optionnel)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Parlez-nous de votre établissement, vos attentes, vos questions..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                />
                <div className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-full font-semibold text-base transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Envoi en cours...
                  </span>
                ) : (
                  'Envoyer ma candidature'
                )}
              </button>

              <p className="text-center text-xs text-gray-400">
                En soumettant ce formulaire, vous acceptez d'être contacté par l'équipe Localygo. Sans engagement.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* CTA final */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-extrabold text-white mb-5">Prêt à développer votre commerce ?</h2>
          <p className="text-teal-100 text-lg mb-10 max-w-2xl mx-auto">
            Rejoignez les 500+ partenaires qui font confiance à Localygo pour toucher leurs clients locaux et augmenter leurs ventes.
          </p>
          <a
            href="#candidature"
            className="inline-block bg-white text-teal-700 hover:bg-teal-50 px-10 py-4 rounded-full font-bold text-base transition-all shadow-lg cursor-pointer whitespace-nowrap"
          >
            Devenir partenaire maintenant
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
